import type { ContentHash, ContentRef } from "@cogo/publisher-core";

import {
  assertApplicationEvidenceRole,
  assertApplicationOntologyGenerationBindingRoleIntegrity,
  assertPlainJsonContract,
  type ApplicationEvidenceRoleResolver,
  type ApplicationOntologyGenerationBinding,
} from "./application-ontology-generation-binding";

export interface ApplicationGenerationEvidenceEnvelope {
  schemaVersion: "cogo.application-generation-evidence-envelope.v1";
  binding: ApplicationOntologyGenerationBinding;
  modelInvocationRef: ContentRef;
  applicationPackageRef: ContentRef;
  artifactRef: ContentRef;
  artifactValidationObservationRef: ContentRef;
  artifactPersistenceEligibilityRef: ContentRef;
  contentHash: ContentHash;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const KEYS = [
  "schemaVersion", "binding", "modelInvocationRef", "applicationPackageRef",
  "artifactRef", "artifactValidationObservationRef",
  "artifactPersistenceEligibilityRef", "contentHash",
] as const;

function plain(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype;
}
function exact(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return plain(value) && Object.keys(value).length === keys.length &&
    Object.keys(value).every((key) => keys.includes(key)) &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}
function reference(value: unknown): value is ContentRef {
  return exact(value, ["ref", "contentHash"]) && typeof value.ref === "string" &&
    value.ref.trim().length > 0 && typeof value.contentHash === "string" &&
    HASH.test(value.contentHash);
}
export function assertApplicationGenerationEvidenceEnvelopeIntegrity(
  value: unknown,
  resolver: ApplicationEvidenceRoleResolver,
): ApplicationGenerationEvidenceEnvelope {
  assertPlainJsonContract(value, "application-generation-evidence-envelope");
  if (!exact(value, KEYS) ||
    value.schemaVersion !== "cogo.application-generation-evidence-envelope.v1" ||
    typeof value.contentHash !== "string" || !HASH.test(value.contentHash)) {
    throw new Error("application-generation-evidence-envelope-invalid");
  }
  assertApplicationOntologyGenerationBindingRoleIntegrity(
    value.binding,
    resolver,
  );
  for (const field of [
    "modelInvocationRef", "applicationPackageRef", "artifactRef",
    "artifactValidationObservationRef", "artifactPersistenceEligibilityRef",
  ] as const) {
    if (!reference(value[field])) {
      throw new Error("application-generation-evidence-envelope-invalid");
    }
  }
  const envelope = value as unknown as ApplicationGenerationEvidenceEnvelope;
  assertApplicationEvidenceRole(resolver, "modelInvocationRef", envelope.modelInvocationRef, "model-invocation");
  assertApplicationEvidenceRole(resolver, "applicationPackageRef", envelope.applicationPackageRef, "application-package");
  assertApplicationEvidenceRole(resolver, "artifactRef", envelope.artifactRef, "generated-artifact");
  assertApplicationEvidenceRole(resolver, "artifactValidationObservationRef", envelope.artifactValidationObservationRef, "artifact-validation-observation");
  assertApplicationEvidenceRole(resolver, "artifactPersistenceEligibilityRef", envelope.artifactPersistenceEligibilityRef, "artifact-persistence-eligibility");
  return envelope;
}
